
import L from "leaflet";

export const DefaultMaxObjectiveSize = 60;
export const DefaultMinObjectiveSize = 10;

// AV-C3: well symbol / colour classification (industry convention, aligned with
// the AV-C2 controlled status set in backend base_data/enums.py). We map the
// per-well EAV elements `well_type` (producer / injector / dry / abandoned …)
// and `producing_status` to a symbol "class" + colour so the map legend reads
// like a petroleum well-spot map (API/PPDM convention) rather than identical
// dots. Unknown/missing -> a neutral "unknown" class (never guessed).
export const WELL_SYMBOL_CLASS = {
  producer: "producer", // oil/gas producing well
  injector: "injector", // water/gas injection / disposal
  dry: "dry", // dry hole
  abandoned: "abandoned", // P&A / TA
  unknown: "unknown",
};

// Status tokens come from base_data/enums.py WELL_STATUS_CHOICES.
const STATUS_ABANDONED = new Set(["P_AND_A", "TA"]);
const STATUS_INJECTING = new Set(["INJECTING"]);

// well_type free-text/enmu values seen in the field -> symbol class.
function normalize(v) {
  return (v == null ? "" : String(v)).trim().toLowerCase();
}

// A coordinate is usable for the map bounds only if it parses to a finite,
// on-earth number and is not the (0,0) null-island placeholder. Used to keep a
// single bad well from stretching fitBounds well past the asset.
export function isValidLatLng(lat, lng) {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  return (
    !isNaN(la) &&
    !isNaN(ln) &&
    Math.abs(la) <= 90 &&
    Math.abs(ln) <= 180 &&
    !(la === 0 && ln === 0)
  );
}

// AV-C3: derive the symbol class from well_type first, then fall back to status.
// Pure classification, no fabrication: anything we don't recognise is "unknown".
export function classifyWell(wellData) {
  const type = normalize(wellData && wellData.well_type);
  const status = (wellData && wellData.producing_status) || "";

  if (STATUS_ABANDONED.has(status)) return WELL_SYMBOL_CLASS.abandoned;

  if (type.includes("inject") || type.includes("disposal") || type.includes("swd"))
    return WELL_SYMBOL_CLASS.injector;
  if (STATUS_INJECTING.has(status)) return WELL_SYMBOL_CLASS.injector;

  if (type.includes("dry") || type.includes("d&a")) return WELL_SYMBOL_CLASS.dry;
  if (type.includes("abandon") || type.includes("p&a") || type.includes("plug"))
    return WELL_SYMBOL_CLASS.abandoned;

  if (
    type.includes("oil") ||
    type.includes("gas") ||
    type.includes("prod") ||
    type.includes("producer") ||
    type.includes("horizontal") ||
    type.includes("vertical")
  )
    return WELL_SYMBOL_CLASS.producer;

  return WELL_SYMBOL_CLASS.unknown;
}

// AV-C3: pull a well-bore trajectory (lateral) from whatever the well object
// already carries, with graceful degradation. Permian survey data is sparse
// (sometimes only 2 stations), so we draw "as many points as exist" and fall
// back to just the wellhead when there is nothing usable.
//
// Accepted shapes (first one present wins):
//   well.trajectory      -> [[lat,lng], ...] or [{latitude,longitude}, ...]
//   well.survey          -> [{latitude,longitude}|{lat,lng}, ...]
//   well.lateral         -> same as trajectory
// A single surface point (or none) -> returns [] (no polyline; wellhead dot only).
export function getWellTrajectory(wellData) {
  if (!wellData) return [];
  const raw =
    wellData.trajectory || wellData.lateral || wellData.survey || null;
  if (!Array.isArray(raw)) return [];

  const path = [];
  for (const p of raw) {
    let lat;
    let lng;
    if (Array.isArray(p)) {
      lat = p[0];
      lng = p[1];
    } else if (p && typeof p === "object") {
      lat = p.latitude != null ? p.latitude : p.lat;
      lng = p.longitude != null ? p.longitude : p.lng;
    }
    lat = parseFloat(lat);
    lng = parseFloat(lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      path.push([lat, lng]);
    }
  }
  // need at least 2 distinct points to draw a line; otherwise wellhead-only.
  return path.length >= 2 ? path : [];
}

// AV-C4: format the selected metric value with its industry unit for the map
// tooltip. Mirrors the AV-A4 KPI口径: GOR in scf/bbl (×1000 from Mscf/bbl),
// water-cut as %. Other products keep their raw value (unit-agnostic here).
export function formatBubbleMetric(productType, value, infinite) {
  if (productType === "avg_oil_gas_prd_rate") {
    if (infinite || value === Infinity) return "GOR: ∞ (oil=0)";
    if (value == null || isNaN(value)) return "GOR: —";
    // value is gas/oil in Mscf/bbl -> ×1000 to scf/bbl (AV-A4 convention)
    return `GOR: ${Math.round(value * 1000).toLocaleString()} scf/bbl`;
  }
  if (productType === "avg_water_cut") {
    if (value == null || isNaN(value)) return "Water-cut: —";
    return `Water-cut: ${(value * 100).toFixed(1)} %`;
  }
  if (value == null || isNaN(value)) return "";
  return `${Number(value).toLocaleString()}`;
}

export function getMapFeatures(productionsData, wellsData, productType) {
	let productionArray = [];
	let points = [];

	for (const wellData of wellsData) {
		let latitude = wellData.latitude; //parseFloat(wellData.latitude);
		let longitude = wellData.longitude; //parseFloat(wellData.longitude);
		// Only feed valid, finite, on-earth, non-(0,0) coordinates into the map
		// bounds. A single missing / zero / garbage coordinate would otherwise
		// stretch fitBounds far past the asset (or out to the whole globe), which
		// is why the map opened zoomed too far out.
		if (isValidLatLng(latitude, longitude)) {
			points.push([parseFloat(latitude), parseFloat(longitude)]);
		}

		// if (wellData.uwi in productionsData === false) {
		//   continue;
		// }
		const productionData = productionsData[wellData.uwi];
		if (productionData !== undefined && productionData != null) {
			let prdValue = productionData[productType];
			// AV-A1: an infinite GOR (oil=0 gas wells) must not be fed into the
			// Math.max bubble-scaling (it would NaN-out every other bubble), but
			// the well must still be rendered. Skip it here; it is rendered at
			// max bubble size below.
			if (
				prdValue !== undefined &&
				prdValue != null &&
				!isNaN(prdValue) &&
				isFinite(prdValue)
			) {
				productionArray.push(prdValue);
			}
		}
	}

	let bounds = points.length > 0 ? L.latLngBounds(points) : null;
	let center = bounds ? bounds.getCenter() : [0.0, 46.036];

	// AV-A1: only bail out when there are no renderable wells at all. Wells
	// with an infinite GOR (oil=0 gas wells) carry no finite value but must
	// still be rendered, so don't treat an all-infinite dataset as empty.
	let hasInfiniteWell = wellsData.some((wellData) => {
		const pd = productionsData[wellData.uwi];
		return pd != null && !isFinite(pd[productType]) && !isNaN(pd[productType]);
	});

	if (productionArray.length === 0 && !hasInfiniteWell) {
		return [[], center, bounds];
	}

	// When every well is infinite, Math.max(...[]) is -Infinity; guard it.
	let productionMax =
		productionArray.length > 0 ? Math.max(...productionArray) : 1;
	let tempFeatures = wellsData
		.map((wellData, index) => {
			if (wellData.uwi in productionsData === false) {
				return null;
			}
			const productionData = productionsData[wellData.uwi];
			let prdValue = productionData[productType];
			if (prdValue == null || prdValue === undefined || isNaN(prdValue)) {
				return null;
			}
			// AV-A1: infinite GOR (oil=0 gas wells) -> keep visible at max bubble
			// size instead of dropping the well or producing an invalid size.
			let infinite = !isFinite(prdValue);
			let bubbleSize = infinite
				? DefaultMaxObjectiveSize
				: computeBubbleSize(
						prdValue,
						productionMax,
						DefaultMinObjectiveSize,
						DefaultMaxObjectiveSize
				  );
			return {
				Name: wellData.well_name,
				Latitude: wellData.latitude,
				Longitude: wellData.longitude,
				Value: bubbleSize,
				apiId: wellData.uwi,
				// AV-C3: symbol class (producer/injector/dry/abandoned) + the
				// well-bore trajectory (lateral) for horizontal wells, degrading
				// gracefully to wellhead-only when survey data is absent.
				SymbolClass: classifyWell(wellData),
				WellType: wellData.well_type != null ? wellData.well_type : null,
				ProducingStatus:
					wellData.producing_status != null
						? wellData.producing_status
						: null,
				Trajectory: getWellTrajectory(wellData),
				// AV-C4: keep the raw metric value + product so the tooltip can
				// show GOR scf/bbl / water-cut % rather than the (meaningless)
				// bubble pixel size.
				MetricValue: prdValue,
				MetricInfinite: infinite,
				ProductType: productType,
			};
		})
		.filter((item) => item != null);

	return [tempFeatures, center, bounds];
}
export function getCrossSectionMapFeatures(wellsData) {
	let points = [];

	for (const wellData of wellsData) {
		if (isValidLatLng(wellData.latitude, wellData.longitude)) {
			points.push([
				parseFloat(wellData.latitude),
				parseFloat(wellData.longitude),
			]);
		}
	}

	let tempFeatures = [];
	for (const wellData of wellsData) {
		const feature = {
			Name: wellData.well_name,
			Latitude: wellData.latitude,
			Longitude: wellData.longitude,
			Value: 30,
			apiId: wellData.uwi,
		};
		tempFeatures.push(feature);
	}
	console.log('points', points);
	let bounds = points && points.length > 0 ? L.latLngBounds(points) : L.latLngBounds([[0, 0], [0, 0]]);
	let center = bounds ? bounds.getCenter() : [0, 0];
	return [tempFeatures, center, bounds];
}

function computeBubbleSize(
	value,
	maxValue,
	defaultMinBubbleSize,
	defaultMaxBubbleSize
) {
	if (maxValue === 0) {
		return 0;
	}

	let size = (value / maxValue) * defaultMaxBubbleSize;
	if (size < defaultMinBubbleSize) {
		size = defaultMinBubbleSize;
	}
	return size;
}

export function compareNumbers(a, b) {
	return a - b;
}

// AV-C5: collapse the per-well CRS list from getAssetCrs into a single label for
// the map. Permian wells can mix NAD27/NAD83 (offset up to ~100 m), so we show
// the distinct datum(s) + UTM zone(s) actually present rather than assuming one.
// Missing datum/zone is shown as "—" (never fabricated to WGS84 — 护栏4).
export function summarizeCrs(crsResponse) {
	const wells =
		crsResponse && Array.isArray(crsResponse.wells) ? crsResponse.wells : [];
	if (wells.length === 0) {
		return { datumLabel: "—", zoneLabel: "—", mixed: false };
	}
	const datums = new Set();
	const zones = new Set();
	let missing = false;
	for (const w of wells) {
		if (w.coord_datum) datums.add(String(w.coord_datum));
		else missing = true;
		if (w.utm_zone) zones.add(String(w.utm_zone));
	}
	const datumArr = [...datums];
	const zoneArr = [...zones];
	const datumLabel =
		datumArr.length === 0
			? "—"
			: datumArr.join(" / ") + (missing ? " (+unknown)" : "");
	const zoneLabel = zoneArr.length === 0 ? "—" : "UTM " + zoneArr.join(" / ");
	return {
		datumLabel,
		zoneLabel,
		mixed: datumArr.length > 1,
	};
}
